import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MarketingNav, MarketingFooter } from '../components/marketing/MarketingChrome';
import './LandingPage.css';
import './MarketingPage.css';

/**
 * /features — mid-funnel commercial-intent page.
 *
 * Why this page exists: before it, the site had exactly one marketing page and a
 * blog. The blog targets purely informational queries ("how to handle a
 * last-minute schedule change"), and the landing page targets brand + category.
 * Nothing targeted the commercial middle — someone who has decided they need a
 * tool and is searching "custody calendar app" or "shared expense tracker for
 * co-parents". Those searchers are far closer to signing up than a blog reader,
 * and they had no page to land on.
 *
 * Every capability described here is verified against the shipped codebase.
 * Platform claims match the landing page's existing meta description (iPhone,
 * iPad, web) — do not add store-availability claims here without checking the
 * actual listing state first.
 */

const SITE_URL = 'https://letscoparent.us';
const CANONICAL = `${SITE_URL}/features`;
const OG_IMAGE = `${SITE_URL}/og-default.png`;

const META_TITLE = 'Features — Custody Calendar, Expense Splitting & Shared Activities | coparent';
const META_DESCRIPTION =
  'Everything in coparent: a shared custody calendar with day swaps and trips, 50/50 or custom expense splitting, kids’ activities, documents, and a message thread built for two households. Free.';

const FEATURE_GROUPS = [
  {
    id: 'custody-calendar',
    eyebrow: 'Custody calendar',
    title: 'The schedule both homes actually agree on',
    body:
      'Pick a repeating pattern and coparent expands it across the year, so nobody counts weeks on a paper calendar. Both parents see the same days in the same colors.',
    points: [
      'Built-in patterns: alternating weeks, 2-2-3, 3-4-4-3, 5-2, every-other-weekend, and full custody',
      'Custom cycles when your arrangement does not match a template',
      'Set the date your schedule starts, so it lines up with your actual agreement',
      'Day swaps: propose a single-day change, and it only moves once the other parent accepts',
      'Trips: block out a vacation range in one step instead of swapping day by day',
    ],
  },
  {
    id: 'expenses',
    eyebrow: 'Shared expenses',
    title: 'Who paid, who owes, and what it was for',
    body:
      'Log a cost once and the running balance updates for both of you. No spreadsheet, no monthly reconciliation argument.',
    points: [
      '50/50 by default, or set a custom split ratio per expense',
      'Categories for the costs that actually recur — medical, school, activities, clothing, childcare',
      'A running balance that shows who owes what, settled on your own terms',
      'Recurring expenses for the predictable monthly ones',
      'Insights that break spending down by category and by child',
      'Receipt attachments, so the detail lives with the expense',
    ],
  },
  {
    id: 'activities',
    eyebrow: 'Activities & logistics',
    title: 'The details that fall through the cracks',
    body:
      'Practices, appointments, and school events belong to the child, not to one parent’s memory. Everything lands on the shared calendar.',
    points: [
      'One-time and recurring activities, tied to specific kids',
      'Chores and household rules that carry across both homes',
      'Document storage for custody agreements, insurance cards, and school forms',
      'A message thread that keeps logistics out of your personal texts',
    ],
  },
  {
    id: 'notifications',
    eyebrow: 'Staying in sync',
    title: 'You find out without having to ask',
    body:
      'coparent tells each parent what changed, so neither of you is the one chasing information.',
    points: [
      'Push notifications when the other parent adds an expense, activity, or message',
      'Swap and trip requests, plus the accept or decline that follows',
      'A handoff reminder the evening before a changeover day',
      'Per-type notification preferences, so you choose what is worth interrupting you',
    ],
  },
];

const FAQS = [
  {
    q: 'Do both parents need an account?',
    a: 'Yes. Each parent has their own login — you are never sharing an account or a password. One parent sets up the family and sends an invite link; the second parent joins in about a minute.',
  },
  {
    q: 'Can I start before my co-parent joins?',
    a: 'Yes. You can build the whole schedule, add your kids, and log expenses on your own. Everything you have already entered is there when the other parent accepts the invite.',
  },
  {
    q: 'What if our schedule does not match a standard pattern?',
    a: 'Build a custom cycle of any length and assign each day to either parent. On top of that, day swaps and trips handle the one-off changes that no repeating pattern can express.',
  },
  {
    q: 'Can the other parent change the schedule without me?',
    a: 'A swap is a proposal, not a change. The day only moves once the other parent accepts it, and both of you see the request and the response.',
  },
  {
    q: 'Where does it run?',
    a: 'On iPhone and iPad, and in any browser. Your data syncs between them in real time, so the calendar you check on your phone is the one your co-parent sees on their laptop.',
  },
];

const FeaturesPage = () => {
  const navigate = useNavigate();

  const softwareSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'coparent',
    applicationCategory: 'LifestyleApplication',
    operatingSystem: 'iOS, Web',
    description: META_DESCRIPTION,
    url: CANONICAL,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    featureList: FEATURE_GROUPS.flatMap((g) => g.points),
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Features', item: CANONICAL },
    ],
  };

  return (
    <div className="landing-page">
      <Helmet>
        <title>{META_TITLE}</title>
        <meta name="description" content={META_DESCRIPTION} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content={META_TITLE} />
        <meta property="og:description" content={META_DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={META_TITLE} />
        <meta name="twitter:description" content={META_DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <MarketingNav />

      <header className="mk-hero">
        <div className="lp-container">
          <p className="mk-eyebrow">Features</p>
          <h1 className="mk-h1">
            Everything two households<br />
            <em>need to stay in sync.</em>
          </h1>
          <p className="mk-lede">
            A shared custody calendar, expense splitting that settles itself, and the
            day-to-day logistics of raising kids across two homes — in one place both
            parents can see.
          </p>
          <div className="mk-actions">
            <button className="lp-btn-primary" onClick={() => navigate('/register')}>
              Get started free
            </button>
            <Link className="lp-btn-secondary" to="/pricing">
              See what it costs
            </Link>
          </div>
          <p className="mk-hero-note">Free to use. About three minutes to set up.</p>
        </div>
      </header>

      <main>
        {FEATURE_GROUPS.map((group, i) => (
          <section
            key={group.id}
            id={group.id}
            className={`mk-section ${i % 2 === 1 ? 'mk-section-alt' : ''}`}
          >
            <div className="lp-container mk-section-inner">
              <div className="mk-section-copy">
                <p className="mk-eyebrow">{group.eyebrow}</p>
                <h2 className="mk-h2">{group.title}</h2>
                <p className="mk-body">{group.body}</p>
              </div>
              <ul className="mk-list">
                {group.points.map((point) => (
                  <li key={point}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        ))}

        <section className="mk-section">
          <div className="lp-container">
            <h2 className="mk-h2 mk-h2-centered">Common questions</h2>
            <div className="mk-faq">
              {FAQS.map((f) => (
                <details key={f.q} className="mk-faq-item">
                  <summary>{f.q}</summary>
                  <p>{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="mk-section mk-section-alt">
          <div className="lp-container mk-related">
            <h2 className="mk-h2">Working it out as you go?</h2>
            <p className="mk-body">
              Plenty of people set up a shared calendar before anything is formal.
              These walk through the parts people ask about most:
            </p>
            <ul className="mk-links">
              <li><Link to="/blog/co-parenting-custody-schedule">How to build a custody schedule that holds up</Link></li>
              <li><Link to="/blog/co-parenting-shared-expenses">Splitting shared expenses without the monthly argument</Link></li>
              <li><Link to="/blog/first-30-days-coparenting-checklist">The first 30 days: a co-parenting checklist</Link></li>
              <li><Link to="/blog">All articles</Link></li>
            </ul>
          </div>
        </section>

        <section className="lp-final-cta">
          <div className="lp-container">
            <div className="lp-final-cta-card">
              <h2 className="mk-h2">Start with the calendar.</h2>
              <p className="mk-body">
                Set up your schedule in a few minutes. Invite your co-parent whenever
                you are ready — everything you have entered will be waiting.
              </p>
              <button className="lp-btn-primary" onClick={() => navigate('/register')}>
                Create a free account
              </button>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
};

export default FeaturesPage;
